// Production endpoint validation for the operator console.
//
// An operator access token has cross-tenant authority. Development may use
// local Keycloak and platform-graph defaults, but a production bundle must
// never silently redirect or attach that token to a loopback/HTTP endpoint
// when its public Vite configuration was omitted.

export type OperatorDeploymentMode = "development" | "production";

export interface OperatorEndpointEnvironment {
  readonly configuredUrl?: string | undefined;
  readonly mode: OperatorDeploymentMode;
}

function isLocalOrUnspecifiedHostname(hostname: string): boolean {
  const normalized = hostname
    .toLowerCase()
    .replace(/^\[|\]$/g, "")
    .replace(/\.$/, "");
  const mappedIpv4HighWord = /^::ffff:([0-9a-f]{1,4}):[0-9a-f]{1,4}$/.exec(
    normalized,
  )?.[1];
  const ipv4Octets = normalized.split(".");
  const isThisNetworkIpv4 =
    ipv4Octets.length === 4 &&
    ipv4Octets.every((octet) => /^\d{1,3}$/.test(octet)) &&
    Number(ipv4Octets[0]) === 0;
  return (
    normalized === "localhost" ||
    normalized.endsWith(".localhost") ||
    normalized === "::1" ||
    normalized === "::" ||
    isThisNetworkIpv4 ||
    /^127(?:\.\d{1,3}){0,3}$/.test(normalized) ||
    (mappedIpv4HighWord !== undefined &&
      [0, 127].includes(Number.parseInt(mappedIpv4HighWord, 16) >> 8))
  );
}

function resolveUrl(
  environment: OperatorEndpointEnvironment,
  envName: string,
  developmentFallback: string,
): URL {
  const configured = environment.configuredUrl?.trim();
  const candidate =
    configured === undefined || configured.length === 0
      ? environment.mode === "development"
        ? developmentFallback
        : undefined
      : configured;
  if (candidate === undefined) {
    throw new Error(`${envName} is required in production`);
  }

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    throw new Error(`${envName} must be a valid absolute URL`);
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`${envName} must use HTTP or HTTPS`);
  }
  if (url.username !== "" || url.password !== "" || url.search !== "" || url.hash !== "") {
    throw new Error(`${envName} must not contain credentials, query, or fragment`);
  }
  if (
    environment.mode === "production" &&
    (url.protocol !== "https:" || isLocalOrUnspecifiedHostname(url.hostname))
  ) {
    throw new Error(`${envName} must use a non-local HTTPS origin in production`);
  }
  return url;
}

/** Resolve the platform-graph API origin before a fetch client can attach an operator token. */
export function resolveOperatorPlatformGraphBaseUrl(
  environment: OperatorEndpointEnvironment,
): string {
  const url = resolveUrl(
    environment,
    "VITE_OPERATOR_PLATFORM_GRAPH_BASE_URL",
    "http://localhost:3000",
  );
  if (url.pathname !== "/") {
    throw new Error(
      "VITE_OPERATOR_PLATFORM_GRAPH_BASE_URL must be an origin without a path",
    );
  }
  return url.origin;
}

/** Resolve the exact operators-realm issuer before AuthProvider can redirect the browser. */
export function resolveOperatorKeycloakAuthority(
  environment: OperatorEndpointEnvironment,
): string {
  const url = resolveUrl(
    environment,
    "VITE_OPERATOR_KEYCLOAK_AUTHORITY",
    "http://localhost:8080/realms/operators",
  );
  const path = url.pathname.replace(/\/+$/, "");
  if (path !== "/realms/operators") {
    throw new Error(
      "VITE_OPERATOR_KEYCLOAK_AUTHORITY must be the operators realm issuer (/realms/operators)",
    );
  }
  return `${url.origin}/realms/operators`;
}
